// =============================================================================
// Writing Tool - V1.5 Simplified Journalist Editor
// A clean, focused writing experience with AI assistance
// =============================================================================

import React, { useEffect, useState } from 'react';
import {
  ArrowLeft,
  Save,
  Loader2,
  Sparkles,
  RotateCcw,
  History,
  Check,
  AlertTriangle,
  ChevronDown,
  Plus,
  FileText,
  Trash2,
  Clock,
  Split,
} from 'lucide-react';
import { diffWords, getDiffStats } from '../utils/diff';
import {
  SimpleStory,
  createSimpleStory,
  getSimpleStory,
  getSimpleStories,
  saveSimpleStory,
  setAiReviewStatus,
  setDraftStatus,
  archiveSimpleStory,
  getVersionHistory,
  restoreVersion,
  SimpleVersion,
} from '../services/simpleStore';
import { runJournalistAgent, runCriticAgent } from '../services/geminiService';
import {
  LanguageCode,
  EditorIssue,
  LANGUAGE_LABELS,
  LANGUAGE_NAMES,
  DEFAULT_LANGUAGE,
} from '../types';

// RTL languages
const RTL_LANGUAGES: LanguageCode[] = ['fa-AF', 'ps-AF'];
const isRTL = (lang: LanguageCode) => RTL_LANGUAGES.includes(lang);

// Detect if text content doesn't match selected language
const detectLanguageMismatch = (text: string, selectedLang: LanguageCode): string | null => {
  if (!text || text.length < 20) return null;

  const arabicRegex = /[\u0600-\u06FF\u0750-\u077F]/g;
  const latinRegex = /[a-zA-Z]/g;

  // Sample first 500 chars
  const sample = text.slice(0, 500);
  const arabicCount = (sample.match(arabicRegex) || []).length;
  const latinCount = (sample.match(latinRegex) || []).length;

  if (selectedLang === 'eng') {
    // English selected but writing in Arabic script
    if (arabicCount > latinCount && arabicCount > 10) {
      return "It looks like you're writing in Dari or Pashto, but 'English' is selected. Please check your language setting.";
    }
  } else {
    // Dari/Pashto selected but writing in Latin script
    if (latinCount > arabicCount && latinCount > 10) {
      return "It looks like you're writing in English, but a local language is selected. Please check your language setting.";
    }
  }
  return null;
};

const WritingTool: React.FC = () => {
  // View state
  const [view, setView] = useState<'list' | 'editor'>('list');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Story list
  const [stories, setStories] = useState<SimpleStory[]>([]);

  // Current story being edited
  const [currentStory, setCurrentStory] = useState<SimpleStory | null>(null);

  // Editor form state
  const [headline, setHeadline] = useState('');
  const [deck, setDeck] = useState('');
  const [body, setBody] = useState('');
  const [language, setLanguage] = useState<LanguageCode>(DEFAULT_LANGUAGE);

  // AI Review state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiLoadingMsg, setAiLoadingMsg] = useState('');
  const [aiIssues, setAiIssues] = useState<EditorIssue[]>([]);
  const [showAiPanel, setShowAiPanel] = useState(false);

  // Diff comparison
  const [originalBody, setOriginalBody] = useState('');
  const [showDiff, setShowDiff] = useState(false);

  // Version history
  const [versions, setVersions] = useState<SimpleVersion[]>([]);
  const [showVersions, setShowVersions] = useState(false);

  // Messages
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Language mismatch warning
  const [languageWarning, setLanguageWarning] = useState<string | null>(null);

  // Load stories on mount
  useEffect(() => {
    loadStories();
  }, []);

  const loadStories = async () => {
    setLoading(true);
    const data = await getSimpleStories({ limit: 50 });
    setStories(data);
    setLoading(false);
  };

  const loadStory = async (id: string) => {
    setLoading(true);
    const story = await getSimpleStory(id);
    if (story) {
      setCurrentStory(story);
      setHeadline(story.headline);
      setDeck(story.deck);
      setBody(story.body);
      setLanguage(story.language);
      setView('editor');

      // Load version history
      const hist = await getVersionHistory(id);
      setVersions(hist);
    }
    setLoading(false);
  };

  const handleNewStory = () => {
    setCurrentStory(null);
    setHeadline('');
    setDeck('');
    setBody('');
    setLanguage(DEFAULT_LANGUAGE);
    setAiIssues([]);
    setShowAiPanel(false);
    setShowDiff(false);
    setOriginalBody('');
    setVersions([]);
    setView('editor');
  };

  const handleSave = async () => {
    if (!headline.trim()) {
      setErrorMsg('Please enter a headline');
      return;
    }

    setSaving(true);
    setErrorMsg(null);

    try {
      if (currentStory) {
        // Update existing
        const updated = await saveSimpleStory(currentStory.id, {
          headline,
          deck,
          body,
        });
        if (updated) {
          setCurrentStory(updated);
          setSuccessMsg('Saved successfully');
          // Refresh version history
          const hist = await getVersionHistory(updated.id);
          setVersions(hist);
        }
      } else {
        // Create new
        const created = await createSimpleStory({
          headline,
          deck,
          body,
          language,
        });
        if (created) {
          setCurrentStory(created);
          setSuccessMsg('Story created');
          // Load version history
          const hist = await getVersionHistory(created.id);
          setVersions(hist);
        }
      }
    } catch (error) {
      setErrorMsg('Failed to save');
      console.error(error);
    } finally {
      setSaving(false);
      setTimeout(() => setSuccessMsg(null), 3000);
    }
  };

  const handleAiReview = async () => {
    if (!body.trim()) {
      setErrorMsg('Please write some content before running AI Review');
      return;
    }

    // Check for language mismatch
    const currentLang = currentStory?.language || language;
    const mismatch = detectLanguageMismatch(body, currentLang);
    if (mismatch) {
      setLanguageWarning(mismatch);
      return; // Don't proceed until user confirms
    }

    await executeAiReview();
  };

  const executeAiReview = async () => {
    setLanguageWarning(null);

    // Save first if new story
    if (!currentStory) {
      await handleSave();
    }

    setAiLoading(true);
    setAiLoadingMsg('Polishing your writing...');
    setShowAiPanel(true);
    setAiIssues([]);
    setErrorMsg(null);

    // Store original for diff
    setOriginalBody(body);

    try {
      // Step 1: Polish with Journalist Agent
      const polished = await runJournalistAgent(headline, deck, body, language, []);

      if (polished) {
        setHeadline(polished.headline || headline);
        setDeck(polished.deck || deck);
        setBody(polished.body || body);
      }

      // Step 2: Critique
      setAiLoadingMsg('Reviewing for issues...');
      const critique = await runCriticAgent(
        polished?.headline || headline,
        polished?.deck || deck,
        polished?.body || body,
        language,
        false
      );

      if (critique?.issues) {
        setAiIssues(
          critique.issues.map((issue, idx) => ({
            id: `issue-${idx}`,
            category: issue.category || 'human_required',
            text: typeof issue === 'string' ? issue : issue.text,
            suggestion: issue.suggestion,
            applied: false,
          }))
        );
      }

      // Update status
      if (currentStory) {
        await setAiReviewStatus(currentStory.id);
        setCurrentStory({ ...currentStory, workflowStatus: 'ai_review' });
      }
    } catch (error) {
      setErrorMsg('AI Review failed. Please try again.');
      console.error(error);
    } finally {
      setAiLoading(false);
      setAiLoadingMsg('');
    }
  };

  const handleApplyFixes = async () => {
    const fixableIssues = aiIssues.filter(
      (i) => i.category === 'ai_fixable' && !i.applied
    );

    if (fixableIssues.length === 0) {
      setErrorMsg('No AI-fixable issues to apply');
      return;
    }

    setAiLoading(true);
    setAiLoadingMsg('Applying fixes...');

    try {
      const polished = await runJournalistAgent(
        headline,
        deck,
        body,
        language,
        fixableIssues.map((i) => i.text)
      );

      if (polished) {
        setHeadline(polished.headline || headline);
        setDeck(polished.deck || deck);
        setBody(polished.body || body);
      }

      // Remove applied issues
      setAiIssues((prev) =>
        prev.filter((i) => i.category !== 'ai_fixable' || i.applied)
      );

      setSuccessMsg(`Applied ${fixableIssues.length} fix(es)`);
    } catch (error) {
      setErrorMsg('Failed to apply fixes');
      console.error(error);
    } finally {
      setAiLoading(false);
      setAiLoadingMsg('');
      setTimeout(() => setSuccessMsg(null), 3000);
    }
  };

  const handleFinishReview = async () => {
    // Save final version
    await handleSave();

    // Reset to draft status
    if (currentStory) {
      await setDraftStatus(currentStory.id);
      setCurrentStory({ ...currentStory, workflowStatus: 'draft' });
    }

    setShowAiPanel(false);
    setShowDiff(false);
    setAiIssues([]);
    setOriginalBody('');
    setSuccessMsg('Review complete!');
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const handleRestoreVersion = async (versionId: string) => {
    if (!currentStory) return;

    setSaving(true);
    const restored = await restoreVersion(currentStory.id, versionId);
    if (restored) {
      setHeadline(restored.headline);
      setDeck(restored.deck);
      setBody(restored.body);
      setCurrentStory(restored);
      setSuccessMsg('Version restored');

      // Refresh history
      const hist = await getVersionHistory(currentStory.id);
      setVersions(hist);
    }
    setSaving(false);
    setShowVersions(false);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const handleArchive = async () => {
    if (!currentStory) return;

    if (!window.confirm('Archive this story? It will be hidden from the list.')) {
      return;
    }

    const success = await archiveSimpleStory(currentStory.id);
    if (success) {
      setSuccessMsg('Story archived');
      setView('list');
      loadStories();
    } else {
      setErrorMsg('Failed to archive');
    }
  };

  const handleBack = () => {
    setView('list');
    setCurrentStory(null);
    setShowAiPanel(false);
    setShowDiff(false);
    setAiIssues([]);
    loadStories();
  };

  const removeIssue = (id: string) => {
    setAiIssues((prev) => prev.filter((i) => i.id !== id));
  };

  const wordCount = body.split(/\s+/).filter(Boolean).length;

  // Determine current language for RTL
  const currentLanguage = currentStory?.language || language;
  const textDirection = isRTL(currentLanguage) ? 'rtl' : 'ltr';

  // ==========================================================================
  // RENDER: Story List View
  // ==========================================================================
  if (view === 'list') {
    return (
      <div className="h-full bg-slate-50 dark:bg-slate-950">
        {/* Header */}
        <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-4">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                Writing Tool
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Your stories with AI assistance
              </p>
            </div>
            <button
              onClick={handleNewStory}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Story
            </button>
          </div>
        </div>

        {/* Story List */}
        <div className="max-w-4xl mx-auto p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : stories.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                No stories yet. Start writing!
              </p>
              <button
                onClick={handleNewStory}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Your First Story
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {stories.map((story) => (
                <button
                  key={story.id}
                  onClick={() => loadStory(story.id)}
                  className="w-full text-left p-4 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-slate-900 dark:text-white truncate">
                        {story.headline || 'Untitled'}
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400 truncate mt-1">
                        {story.deck || 'No summary'}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <span className="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded">
                        {LANGUAGE_LABELS[story.language]}
                      </span>
                      <span
                        className={`px-2 py-1 text-xs rounded ${
                          story.workflowStatus === 'ai_review'
                            ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                        }`}
                      >
                        v{story.versionNumber}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center mt-2 text-xs text-slate-400 dark:text-slate-500">
                    <Clock className="h-3 w-3 mr-1" />
                    {new Date(story.updatedAt).toLocaleDateString()}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==========================================================================
  // RENDER: Editor View
  // ==========================================================================
  return (
    <div className="h-full flex flex-col bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-slate-900 dark:text-white">
                {currentStory ? 'Edit Story' : 'New Story'}
              </h1>
              {currentStory && (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Version {currentStory.versionNumber} • {LANGUAGE_NAMES[currentStory.language]}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Language selector (only for new stories) */}
            {!currentStory && (
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value as LanguageCode)}
                className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              >
                <option value="fa-AF">{LANGUAGE_NAMES['fa-AF']}</option>
                <option value="ps-AF">{LANGUAGE_NAMES['ps-AF']}</option>
                <option value="eng">{LANGUAGE_NAMES['eng']}</option>
              </select>
            )}

            {/* Version History */}
            {currentStory && versions.length > 1 && (
              <div className="relative">
                <button
                  onClick={() => setShowVersions(!showVersions)}
                  className="flex items-center px-3 py-2 text-sm bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700"
                >
                  <History className="h-4 w-4 mr-1.5" />
                  History
                  <ChevronDown className="h-3 w-3 ml-1" />
                </button>

                {showVersions && (
                  <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                    {versions.map((v) => (
                      <button
                        key={v.id}
                        onClick={() => handleRestoreVersion(v.id)}
                        disabled={v.id === currentStory.versionId}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 border-b border-slate-100 dark:border-slate-800 last:border-0 ${
                          v.id === currentStory.versionId
                            ? 'bg-blue-50 dark:bg-blue-900/20'
                            : ''
                        }`}
                      >
                        <div className="flex justify-between">
                          <span className="font-medium">v{v.versionNumber}</span>
                          <span className="text-slate-400 text-xs">
                            {new Date(v.createdAt).toLocaleTimeString()}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 truncate">
                          {v.headline || 'No headline'}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Archive */}
            {currentStory && (
              <button
                onClick={handleArchive}
                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                title="Archive story"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}

            {/* AI Review Button */}
            {!showAiPanel && (
              <button
                onClick={handleAiReview}
                disabled={aiLoading || !body.trim()}
                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                AI Review
              </button>
            )}

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={saving || !headline.trim()}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      {errorMsg && (
        <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="h-4 w-4 text-red-500 mr-2" />
            <span className="text-sm text-red-700 dark:text-red-400">{errorMsg}</span>
          </div>
          <button onClick={() => setErrorMsg(null)} className="text-red-500">×</button>
        </div>
      )}
      {successMsg && (
        <div className="mx-6 mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <Check className="h-4 w-4 text-green-500 mr-2" />
            <span className="text-sm text-green-700 dark:text-green-400">{successMsg}</span>
          </div>
          <button onClick={() => setSuccessMsg(null)} className="text-green-500">×</button>
        </div>
      )}

      {/* Language Mismatch Warning Modal */}
      {languageWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl max-w-md p-6 mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-6 w-6 text-amber-500 mr-3" />
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Language Mismatch
              </h3>
            </div>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {languageWarning}
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setLanguageWarning(null)}
                className="flex-1 px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700"
              >
                Go Back & Fix
              </button>
              <button
                onClick={() => executeAiReview()}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Continue Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Editor */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Diff toolbar */}
          {showAiPanel && (
            <div className="bg-white dark:bg-slate-900 px-6 py-2 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
              <span className="text-sm text-slate-600 dark:text-slate-400">
                {showDiff ? 'Comparing changes' : 'AI-polished draft'}
              </span>
              <div className="flex items-center space-x-2">
                {showDiff && originalBody && (
                  <span className="text-xs text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                    {(() => {
                      const diffs = diffWords(originalBody, body);
                      const stats = getDiffStats(diffs);
                      return (
                        <>
                          <span className="text-green-600">+{stats.added}</span> /{' '}
                          <span className="text-red-500">-{stats.removed}</span>
                        </>
                      );
                    })()}
                  </span>
                )}
                <button
                  onClick={() => setShowDiff(!showDiff)}
                  className={`text-xs px-3 py-1 rounded ${
                    showDiff
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  <Split className="h-3 w-3 inline mr-1" />
                  {showDiff ? 'Hide Changes' : 'Show Changes'}
                </button>
              </div>
            </div>
          )}

          {/* Editor fields */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-3xl mx-auto space-y-6">
              {/* Headline */}
              <input
                type="text"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                disabled={showDiff}
                placeholder="Headline"
                dir={textDirection}
                className="w-full text-2xl font-bold border-none p-0 focus:ring-0 text-slate-900 dark:text-white placeholder-slate-300 dark:placeholder-slate-700 bg-transparent"
              />

              {/* Deck/Summary */}
              <textarea
                value={deck}
                onChange={(e) => setDeck(e.target.value)}
                disabled={showDiff}
                rows={2}
                placeholder="Summary / Deck..."
                dir={textDirection}
                className="w-full text-lg text-slate-600 dark:text-slate-400 border-none p-0 focus:ring-0 resize-none italic bg-transparent"
              />

              <div className="w-full h-px bg-slate-200 dark:bg-slate-800" />

              {/* Body - diff view or editable */}
              {showDiff && originalBody ? (
                <div className="text-lg leading-relaxed whitespace-pre-wrap" dir={textDirection}>
                  {diffWords(originalBody, body).map((part, i) => {
                    if (part.added)
                      return (
                        <span key={i} className="bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 px-0.5 rounded">
                          {part.value}{' '}
                        </span>
                      );
                    if (part.removed)
                      return (
                        <span key={i} className="bg-red-50 dark:bg-red-900/30 text-red-400 line-through opacity-60">
                          {part.value}{' '}
                        </span>
                      );
                    return <span key={i} className="text-slate-800 dark:text-slate-200">{part.value} </span>;
                  })}
                </div>
              ) : (
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Start writing your story..."
                  dir={textDirection}
                  className="w-full min-h-[400px] text-lg leading-relaxed text-slate-800 dark:text-slate-200 placeholder-slate-300 dark:placeholder-slate-700 border-none p-0 focus:ring-0 resize-none bg-transparent"
                />
              )}

              {/* Word count */}
              <div className="text-xs text-slate-400 dark:text-slate-500 pt-4 border-t border-slate-100 dark:border-slate-800">
                {wordCount} words
              </div>
            </div>
          </div>
        </div>

        {/* AI Review Panel */}
        {showAiPanel && (
          <div className="w-80 flex-shrink-0 flex flex-col bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800">
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800">
              <h3 className="font-semibold text-slate-800 dark:text-slate-200 text-sm flex items-center">
                <Sparkles className="h-4 w-4 mr-2 text-purple-500" />
                AI Review
              </h3>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {aiLoading ? (
                <div className="flex flex-col items-center justify-center py-10">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-500 mb-4" />
                  <p className="text-sm text-slate-600 dark:text-slate-400">{aiLoadingMsg}</p>
                </div>
              ) : aiIssues.length === 0 ? (
                <div className="text-center py-10">
                  <Check className="h-8 w-8 mx-auto text-green-500 mb-2" />
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    No issues found!
                  </p>
                </div>
              ) : (
                <>
                  {/* AI Fixable */}
                  {aiIssues.filter((i) => i.category === 'ai_fixable').length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center mb-2">
                        <Sparkles className="h-3.5 w-3.5 text-yellow-500 mr-1.5" />
                        <span className="text-xs font-semibold text-yellow-700 dark:text-yellow-400 uppercase">
                          AI Can Fix ({aiIssues.filter((i) => i.category === 'ai_fixable').length})
                        </span>
                      </div>
                      {aiIssues
                        .filter((i) => i.category === 'ai_fixable')
                        .map((issue) => (
                          <div key={issue.id} className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg mb-2 group">
                            <div className="flex justify-between">
                              <p className="text-sm text-slate-700 dark:text-slate-300">{issue.text}</p>
                              <button
                                onClick={() => removeIssue(issue.id!)}
                                className="text-yellow-400 hover:text-red-500 opacity-0 group-hover:opacity-100"
                              >
                                ×
                              </button>
                            </div>
                            {issue.suggestion && (
                              <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-1 italic">
                                💡 {issue.suggestion}
                              </p>
                            )}
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Human Required */}
                  {aiIssues.filter((i) => i.category === 'human_required').length > 0 && (
                    <div>
                      <div className="flex items-center mb-2">
                        <AlertTriangle className="h-3.5 w-3.5 text-orange-500 mr-1.5" />
                        <span className="text-xs font-semibold text-orange-700 dark:text-orange-400 uppercase">
                          Needs Your Attention ({aiIssues.filter((i) => i.category === 'human_required').length})
                        </span>
                      </div>
                      {aiIssues
                        .filter((i) => i.category === 'human_required')
                        .map((issue) => (
                          <div key={issue.id} className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg mb-2 group">
                            <div className="flex justify-between">
                              <p className="text-sm text-slate-700 dark:text-slate-300">{issue.text}</p>
                              <button
                                onClick={() => removeIssue(issue.id!)}
                                className="text-orange-400 hover:text-red-500 opacity-0 group-hover:opacity-100"
                              >
                                ×
                              </button>
                            </div>
                            {issue.suggestion && (
                              <p className="text-xs text-orange-600 dark:text-orange-500 mt-1 italic">
                                💡 {issue.suggestion}
                              </p>
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Action buttons */}
            <div className="p-4 border-t border-slate-200 dark:border-slate-800 space-y-2">
              {aiIssues.filter((i) => i.category === 'ai_fixable').length > 0 && (
                <button
                  onClick={handleApplyFixes}
                  disabled={aiLoading}
                  className="w-full py-2 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-900/30 rounded-lg text-sm font-medium hover:bg-yellow-100 dark:hover:bg-yellow-900/30 disabled:opacity-50"
                >
                  <Sparkles className="h-4 w-4 inline mr-2" />
                  Apply AI Fixes
                </button>
              )}

              <button
                onClick={() => {
                  setShowAiPanel(false);
                  setShowDiff(false);
                  setAiIssues([]);
                }}
                className="w-full py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-sm hover:bg-slate-200 dark:hover:bg-slate-700"
              >
                <RotateCcw className="h-4 w-4 inline mr-2" />
                Continue Editing
              </button>

              <button
                onClick={handleFinishReview}
                className="w-full py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                <Check className="h-4 w-4 inline mr-2" />
                Done
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white/50 dark:bg-slate-900/50 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      )}
    </div>
  );
};

export default WritingTool;
